import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, CheckCircle2 } from 'lucide-react';

const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
    }
};

const StrategyPage = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        const formData = new FormData(e.target);

        try {
            const response = await fetch("https://api.web3forms.com/submit", {
                method: "POST",
                body: formData
            });

            const data = await response.json();

            if (data.success) {
                setSubmitStatus("success");
                e.target.reset();
            } else {
                console.error("Web3Forms Error:", data);
                setErrorMessage(data.message || "Something went wrong.");
                setSubmitStatus("error");
            }
        } catch (error) {
            console.error("Fetch Error:", error);
            setErrorMessage(error.message || "Network error occurred.");
            setSubmitStatus("error");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <main className="pt-24 min-h-screen">
            <section className="relative py-20 px-6 overflow-hidden max-w-4xl mx-auto">
                {/* Intense orange glow */}
                <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[1400px] h-[1400px] bg-[radial-gradient(ellipse_at_center,_rgba(255,85,0,0.35)_0%,_rgba(220,40,0,0.15)_40%,_rgba(120,15,10,0.05)_70%,_transparent_100%)] blur-[120px] pointer-events-none" />

                {/* Grain Overlay */}
                <div className="bg-grain opacity-[0.05]" />

                <div className="relative">
                    <div className="text-center mb-16">
                        <motion.h1
                            initial="hidden" animate="visible" variants={fadeInUp}
                            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-[-0.02em] font-sans uppercase leading-[1.1]"
                        >
                            Start with strategy
                        </motion.h1>
                        <motion.p initial="hidden" animate="visible" variants={fadeInUp} className="text-[#A0A0A0] text-lg leading-relaxed max-w-xl mx-auto">
                            You can choose to book a strategy call with Manish (Founder & Lead Strategist, Aligned Studio), or we'll follow up via email.
                        </motion.p>
                    </div>

                    {submitStatus === "success" ? (
                        <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="bg-[#111111] border border-[#FF5A1F]/30 p-12 rounded-[24px] shadow-2xl text-center">
                            <div className="w-16 h-16 bg-[#FF5A1F]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                <CheckCircle2 className="w-8 h-8 text-[#FF5A1F]" />
                            </div>
                            <h2 className="text-2xl font-bold text-white mb-4">Request sent successfully.</h2>
                            <p className="text-[#A0A0A0]">We'll review your details and get back to you shortly.</p>
                        </motion.div>
                    ) : (
                        <motion.form
                            initial="hidden" animate="visible" variants={fadeInUp}
                            onSubmit={handleSubmit}
                            className="bg-[#111111] border border-white/[0.04] p-8 md:p-12 rounded-[24px] shadow-2xl space-y-6"
                        >
                            {/* Web3Forms required fields */}
                            <input type="hidden" name="access_key" value="37629d38-a297-40fa-8aaa-3ec44b16143e" />
                            <input type="hidden" name="subject" value="New Strategy Form Submission - Aligned Studio" />
                            <input type="hidden" name="from_name" value="Aligned Studio Strategy Form" />

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-[#F8F8F8] uppercase tracking-wide">Full name</label>
                                <input
                                    type="text"
                                    name="name"
                                    required
                                    className="w-full bg-[#1A1A1A] border border-white/[0.05] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#FF5A1F] transition-colors"
                                    placeholder="John Doe"
                                />
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-[#F8F8F8] uppercase tracking-wide">Email address</label>
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        className="w-full bg-[#1A1A1A] border border-white/[0.05] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#FF5A1F] transition-colors"
                                        placeholder="john@company.com"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-[#F8F8F8] uppercase tracking-wide">Phone number</label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        required
                                        className="w-full bg-[#1A1A1A] border border-white/[0.05] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#FF5A1F] transition-colors"
                                        placeholder="+1 (555) 000-0000"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-[#F8F8F8] uppercase tracking-wide">What is your budget?</label>
                                <select
                                    name="budget"
                                    required
                                    className="w-full bg-[#1A1A1A] border border-white/[0.05] rounded-xl px-4 py-3 text-[#A0A0A0] focus:outline-none focus:border-[#FF5A1F] transition-colors appearance-none"
                                >
                                    <option value="" disabled selected>Select a budget range</option>
                                    <option value="$5k - $10k">$5k - $10k</option>
                                    <option value="$10k - $25k">$10k - $25k</option>
                                    <option value="$25k - $50k">$25k - $50k</option>
                                    <option value="$50k+">$50k+</option>
                                </select>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-[#F8F8F8] uppercase tracking-wide">
                                    Briefly describe what you're building, what's not working today, and what you want to improve.
                                </label>
                                <textarea
                                    name="project_description"
                                    required
                                    className="w-full bg-[#1A1A1A] border border-white/[0.05] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#FF5A1F] transition-colors min-h-[120px] resize-y"
                                    placeholder="Tell us about your project..."
                                ></textarea>
                            </div>

                            {submitStatus === "error" && (
                                <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-xl">
                                    <p className="text-red-400 text-sm font-medium">Submission Failed:</p>
                                    <p className="text-red-400/80 text-sm">{errorMessage}</p>
                                    {errorMessage.toLowerCase().includes("access key") && (
                                        <p className="text-red-400/80 text-sm mt-2">
                                            Make sure you have replaced "YOUR_ACCESS_KEY_HERE" with a valid Web3Forms access key in your code!
                                        </p>
                                    )}
                                </div>
                            )}

                            <div className="pt-4">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full cta-premium py-4 text-white font-semibold rounded-xl inline-flex items-center justify-center gap-2 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting ? "Submitting..." : (
                                        <>Submit Request <ArrowUpRight className="w-5 h-5" /></>
                                    )}
                                </button>
                            </div>
                        </motion.form>
                    )}
                </div>
            </section>
        </main>
    );
};

export default StrategyPage;
